import { KycStatus } from '@getstep/proto/common'
import { Feature } from '@getstep/proto/feature'
import { when } from 'mobx'
import { useRouter } from 'next/router'
import type { FunctionComponent, ReactNode } from 'react'
import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { SsoType } from '@app-types/sso'
import { snackbarStore } from '@lib/store/ui/snackbar'
import { useAuthorizedStores } from '@lib/store/useStores'
import { FundingStage } from '@lib/utils/funding'
import type { KycFormValues } from '@lib/utils/kyc'
import { PerformKycStatus } from '@lib/utils/kyc'
import { renderTemplate, TEXT } from '@lib/utils/text'
import type { LocalLink } from '@shared/util/link'
import { LINK } from '@shared/util/link'

/**
 * This form lets the user enter and verify their phone number.
 */
export const IdentityForm: FunctionComponent<{ children: ReactNode }> = ({
    children,
}) => {
    const {
        sponsorRequestStore,
        kycStore: { defaultValues, performKYC },
        familyStore,
        userStore,
        featureStore,
        sessionStore,
    } = useAuthorizedStores()

    const router = useRouter()

    const methods = useForm<KycFormValues>({
        defaultValues,
    })

    const navigate = (link: LocalLink) => router.push(link.serialize(router))

    const { handleSubmit } = methods

    const next = async (status: KycStatus): Promise<void> => {
        if (status !== KycStatus.SUCCESS) {
            navigate(LINK.kycResult(status))
            return
        }

        if (!sponsorRequestStore.hasSponsees) {
            navigate(LINK.kycResult(KycStatus.SUCCESS))
            return
        }

        // load the family members so we can find the sponsee
        await familyStore.load()

        // find sponsee in family members list
        const sponsee = familyStore?.members.find(
            ({ user, group }) =>
                user.id !== userStore.user.id && group === 'FAMILY_MEMBER',
        )

        const webFundingEnabled = featureStore.isEnabled(
            Feature.WEB_SPONSORSHIP_FUNDING,
        )

        // if sponsee exists and webfunding is enabled, redirect to funding page
        if (sponsee && webFundingEnabled) {
            snackbarStore.show(
                renderTemplate(TEXT.kycSuccessFunding, {
                    firstName:
                        sponsee?.user.legalName?.firstName ?? TEXT.yourTeen,
                }),
                'kyc-success.funding-redirect',
            )
            navigate(LINK.funding(sponsee.user.id, FundingStage.Amount))
            return
        }

        navigate(LINK.kycResult(KycStatus.SUCCESS))
    }

    const submit = handleSubmit(async (values) => {
        try {
            const result = await performKYC(values)

            switch (result.status) {
                case PerformKycStatus.COMPLETED: {
                    return await next(result.kyc)
                }

                case PerformKycStatus.COMPLETED_WITHOUT_INVITE: {
                    return await next(result.kyc)
                }

                case PerformKycStatus.ALREADY_KYCED: {
                    if (userStore.personaFlow?.inquiry?.id) {
                        router.push(LINK.kycCheck().serialize())
                        return
                    }

                    snackbarStore.show(
                        TEXT.kycAlreadyPerformed,
                        'kyc.alreadyPerformed',
                    )
                    return await next(KycStatus.SUCCESS)
                }

                case PerformKycStatus.ERROR: {
                    return snackbarStore.show(
                        result.error.userFriendlyDescription,
                        'kyc.error',
                    )
                }

                case PerformKycStatus.ATTEMPTED: {
                    snackbarStore.show(TEXT.kycInvalid, 'kyc.invalid')
                    for (const field of result.fields) {
                        methods.setError(field, { type: 'validate' })
                    }
                    return
                }

                case PerformKycStatus.INVALID: {
                    if (userStore.personaFlow?.inquiry?.id) {
                        router.push(LINK.kycCheck().serialize())
                        return
                    }

                    return await next(result.kyc)
                }

                default: {
                    return await next(KycStatus.FAILED)
                }
            }
        } catch (e) {
            return snackbarStore.show(TEXT.error, 'kyc.error')
        }
    })

    useEffect(() => {
        const load = async () => {
            await userStore.refresh()

            return when(
                () => sessionStore.loaded && userStore.loaded,
                () => {
                    if (!sessionStore.hasVerifiedEmail) {
                        router.push(
                            LINK.sso(
                                SsoType.Email,
                                LINK.kyc().serialize(),
                            ).serialize(),
                        )
                    }
                },
            )
        }

        load()
    }, [])

    return (
        <FormProvider {...methods}>
            <form onSubmit={submit} data-testid='kyc.form'>
                {children}
            </form>
        </FormProvider>
    )
}
